import { apiClient, ApiRequestError } from '@/api/client';
import { http } from '@/api/http';

/** 与后端约定：0 / 1 / 2，具体含义以后端为准。 */
export type UserGender = 0 | 1 | 2;

/** POST `/system/user/update_info`。仅允许修改以下字段，不包含 email。 */
export interface UpdateUserInfoBody {
  name?: string;
  sysname?: string;
  mobile?: string;
  gender?: UserGender;
  postal_code?: string;
}

/**
 * 当前是否已加入团队（用于空态提醒）。
 * 注意：后端 `team_sub_status` 实际是订阅状态（0/1/2），入团请用 `team_role` / `team_info` / `user_teams`。
 */
export type TeamMembershipStatus = 'not_in_team' | 'in_team';

/** 订阅展示状态（由到期时间等推导，与入团状态分离） */
export type SubscriptionUiStatus = 'active' | 'expired' | 'none';

/** 每个账号最多加入的团队数（含自己创建的），与后端 `MAX_TEAMS_PER_OPERATOR` 一致 */
export const MAX_TEAMS_PER_OPERATOR = 5;

/** `get_user_info.user_teams` 单条：用户所属团队 */
export interface UserTeamMembership {
  teamId: number | string;
  teamName: string;
  teamCode?: string;
  /** 1 = 团长，2 = 成员 */
  role: 1 | 2;
  joinTime?: string;
  expireTime?: string | null;
  foundedAt?: string;
  state?: number;
  isAdmin?: boolean;
  phone?: string;
  email?: string;
  address?: string;
  country?: string;
  logoPath?: string;
  tagline?: string;
}

/** `POST /system/get_user_info` 响应 `data` 字段，与后端 loginAPI 一致。 */
export interface SystemUserInfo {
  userID: number | string;
  doctor_name: string;
  group_id: number | string;
  mobile: string;
  email: string;
  module: Record<string, string>;
  gender: number;
  sys_name: string;
  group_name: string;
  create_date: string;
  share_code: string;
  avatar?: string;
  type: number | string;
  is_admin: boolean;
  /** 自己创建的团队信息；未创建时为空对象 */
  team_info: Record<string, unknown>;
  /** 是否团队管理员：1 / true 为管理员，0 / false 为工作人员 */
  team_admin?: number | boolean | string;
  /**
   * 当前活跃团队角色：0 未入团，1 管理员，2 成员
   *（`loginAPI.get_user_info`）
   */
  team_role?: number | string;
  /** 订阅到期时间，`POST /system/get_user_info` */
  expire_time?: string;
  /**
   * 订阅状态：0 未订阅，1 已订阅，2 已过期
   *（历史前端曾误当作入团状态，请用 `isUserInTeam` / `resolveTeamMembershipStatus`）
   */
  team_sub_status?: number | string;
  team_sub_expire_time?: string;
  team_sub_start_time?: string;
  /** 订阅单位：0 月，1 年（与 create_sub_order 一致） */
  team_sub_unit?: number | string;
  /** 在线状态：`1` 在线，`0` 离线；无服务人员记录时为 `false` */
  online?: number | boolean | Record<string, unknown>;
  /** 服务区域邮政编码（AU/NZ），旧的单个字段 */
  postal_code?: string;
  /** 服务人员负责的邮编列表，`POST /system/get_user_info` */
  postal_code_lst?: unknown;
  /** 用户所属全部团队（含角色），最多 `MAX_TEAMS_PER_OPERATOR` 个 */
  user_teams?: unknown;
  /** 是否已使用免费体验（1 次/账号） */
  is_free_trial_used?: number | boolean | string;
  /** 是否已创建过自己的唯一团队 */
  has_created_team?: number | boolean | string;
  /** 是否有本人有效的建团订阅（不含加入他人团队的订阅） */
  has_own_subscription?: number | boolean | string;
  /** 是否还可激活免费体验 */
  can_activate_free_trial?: number | boolean | string;
}

/** 从 `get_user_info.online` 解析是否在线（可被派单） */
export function resolveUserOnlineStatus(
  profile: SystemUserInfo | null | undefined,
): boolean {
  if (!profile) return true;
  const raw = profile.online;
  if (raw === false) return false;
  if (raw === true) return true;
  if (raw === 0 || raw === '0') return false;
  if (raw === 1 || raw === '1') return true;
  if (typeof raw === 'object' && raw !== null) {
    const nested = (raw as Record<string, unknown>).online;
    if (nested === 0 || nested === '0') return false;
    if (nested === 1 || nested === '1') return true;
  }
  return true;
}

function readNumFlag(source: Record<string, unknown> | null | undefined, keys: string[]): number | undefined {
  if (!source) return undefined;
  for (const key of keys) {
    const raw = source[key];
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    if (typeof raw === 'string' && raw.trim() !== '' && !Number.isNaN(Number(raw))) return Number(raw);
  }
  return undefined;
}

function readDateString(source: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const raw = source[key];
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
  }
  return undefined;
}

function parseUserTeamRow(row: unknown): UserTeamMembership | null {
  if (!row || typeof row !== 'object') return null;
  const o = row as Record<string, unknown>;
  const teamId = o.team_id ?? o.teamId ?? o.id;
  if (teamId === undefined || teamId === null || teamId === '') return null;
  const roleRaw = readNumFlag(o, ['role', 'team_role', 'teamRole']);
  const role: 1 | 2 = roleRaw === 1 ? 1 : 2;
  const name =
    (typeof o.team_name === 'string' && o.team_name.trim())
    || (typeof o.teamName === 'string' && o.teamName.trim())
    || (typeof o.name === 'string' && o.name.trim())
    || '';
  const nestedInfo =
    o.team_info && typeof o.team_info === 'object'
      ? (o.team_info as Record<string, unknown>)
      : undefined;
  const isAdmin = readBoolFlag(o, ['is_admin', 'isAdmin']);
  return {
    teamId: typeof teamId === 'number' || typeof teamId === 'string' ? teamId : String(teamId),
    teamName: name || `Team ${String(teamId)}`,
    teamCode:
      typeof o.team_code === 'string'
        ? o.team_code
        : typeof o.teamCode === 'string'
          ? o.teamCode
          : undefined,
    role,
    joinTime: readDateString(o, ['join_time', 'joinTime']),
    expireTime: readDateString(o, ['expire_time', 'expireTime']) ?? null,
    foundedAt:
      readDateString(o, ['create_date', 'createDate', 'foundedAt'])
      || (nestedInfo ? readTeamInfoString(nestedInfo, ['create_date', 'foundedAt']) : undefined),
    state: readNumFlag(o, ['state']),
    isAdmin: isAdmin === true || (isAdmin === undefined && role === 1),
    phone:
      (typeof o.phone === 'string' && o.phone.trim())
      || (nestedInfo ? readTeamInfoString(nestedInfo, ['phone']) : undefined)
      || undefined,
    email:
      (typeof o.email === 'string' && o.email.trim())
      || (nestedInfo ? readTeamInfoString(nestedInfo, ['email']) : undefined)
      || undefined,
    address:
      (typeof o.address === 'string' && o.address.trim())
      || (typeof o.fullAddress === 'string' && o.fullAddress.trim())
      || (nestedInfo ? readTeamInfoString(nestedInfo, ['address', 'fullAddress']) : undefined)
      || undefined,
    country:
      (typeof o.country === 'string' && o.country.trim())
      || (nestedInfo ? readTeamInfoString(nestedInfo, ['country']) : undefined)
      || undefined,
    logoPath:
      (typeof o.logo_path === 'string' && o.logo_path.trim())
      || (typeof o.logoPath === 'string' && o.logoPath.trim())
      || (nestedInfo ? readTeamInfoString(nestedInfo, ['logo_path', 'logoPath']) : undefined)
      || undefined,
    tagline:
      (typeof o.team_profile === 'string' && o.team_profile.trim())
      || (typeof o.tagline === 'string' && o.tagline.trim())
      || (nestedInfo ? readTeamInfoString(nestedInfo, ['team_profile', 'tagline']) : undefined)
      || undefined,
  };
}

/** 解析 `get_user_info.user_teams` */
export function getUserTeams(profile: SystemUserInfo | null | undefined): UserTeamMembership[] {
  if (!profile) return [];
  const raw = profile.user_teams;
  if (!Array.isArray(raw)) return [];
  return raw.map(parseUserTeamRow).filter((x): x is UserTeamMembership => x !== null);
}

/** 当前账号已加入的团队数量（含自己创建的） */
export function getUserTeamCount(profile: SystemUserInfo | null | undefined): number {
  return getUserTeams(profile).length;
}

/** 是否已经创建过自己的团队：以 `team_info` 是否有数据为准（不含加入他人团队） */
export function hasCreatedOwnTeam(profile: SystemUserInfo | null | undefined): boolean {
  if (!profile) return false;
  return !isTeamInfoEmpty(profile.team_info);
}

/**
 * 解析是否已入团。
 * 优先 `team_role` / `team_info` / `user_teams`；兼容旧调用传入的 `team_sub_status` 原始值时仅把明确 in_team 字符串当入团。
 */
export function resolveTeamMembershipStatus(
  rawOrProfile?: unknown,
): TeamMembershipStatus {
  if (rawOrProfile && typeof rawOrProfile === 'object' && !Array.isArray(rawOrProfile)) {
    const maybeProfile = rawOrProfile as SystemUserInfo;
    if (
      'team_info' in maybeProfile
      || 'user_teams' in maybeProfile
      || 'team_role' in maybeProfile
      || 'userID' in maybeProfile
    ) {
      return isUserInTeam(maybeProfile) ? 'in_team' : 'not_in_team';
    }
  }

  if (rawOrProfile === null || rawOrProfile === undefined || rawOrProfile === '') {
    return 'not_in_team';
  }
  const s = String(rawOrProfile).trim().toLowerCase();
  if (s === 'in_team' || s === 'joined') return 'in_team';
  // 数字：后端 team_role 1/2 为入团；勿把订阅状态 1 当成入团
  return 'not_in_team';
}

/** 根据订阅到期时间推导订阅卡片状态（仅用于已入团后的订阅展示） */
export function resolveSubscriptionUiStatus(
  profile: SystemUserInfo | null | undefined,
): SubscriptionUiStatus {
  if (!profile || !isUserInTeam(profile)) {
    return 'none';
  }
  const expireAt = getTeamSubExpireAt(profile);
  if (!expireAt) return 'active';
  const exp = new Date(expireAt);
  if (Number.isNaN(exp.getTime())) return 'active';
  return exp.getTime() > Date.now() ? 'active' : 'expired';
}

/** `team_info` 为空对象、缺失或无可展示字段时视为无团队信息 */
export function isTeamInfoEmpty(teamInfo: Record<string, unknown> | null | undefined): boolean {
  if (!teamInfo || typeof teamInfo !== 'object') return true;
  const keys = Object.keys(teamInfo);
  if (keys.length === 0) return true;
  return !keys.some((key) => {
    const value = teamInfo[key];
    if (value === null || value === undefined || value === '') return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (typeof value === 'number' || typeof value === 'boolean') return true;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value as object).length > 0;
    return true;
  });
}

function readTeamInfoString(teamInfo: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = teamInfo[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return undefined;
}

function readTeamAdminFlag(profile: SystemUserInfo): boolean {
  const record = profile as unknown as Record<string, unknown>;
  const top = readBoolFlag(record, ['team_admin', 'teamAdmin']);
  if (top !== undefined) return top;
  const nested = readBoolFlag(
    profile.team_info && typeof profile.team_info === 'object' ? profile.team_info : undefined,
    ['team_admin', 'teamAdmin'],
  );
  return nested === true;
}

function readTeamAdminId(profile: SystemUserInfo): string | null {
  const raw = profile.team_info?.admin_id;
  if (raw === null || raw === undefined || raw === '') return null;
  return String(raw);
}

/** 判断指定操作人员是否为团队管理员（团长） */
export function isServicerTeamAdmin(
  operatorId: string | number | null | undefined,
  teamAdminId: string | null | undefined,
): boolean {
  if (operatorId === null || operatorId === undefined || operatorId === '') return false;
  if (!teamAdminId) return false;
  return String(operatorId) === String(teamAdminId);
}

export function getTeamAdminId(profile: SystemUserInfo | null | undefined): string | null {
  if (!profile || isTeamInfoEmpty(profile.team_info)) return null;
  return readTeamAdminId(profile);
}

/**
 * 团队管理员：以 `get_user_info.team_admin` 为准（1 / true 为管理员）。
 */
export function isTeamAdmin(profile: SystemUserInfo | null | undefined): boolean {
  if (!profile) return false;
  return readTeamAdminFlag(profile);
}

function isCreatedTeam(
  profile: SystemUserInfo,
  teamId: string,
): boolean {
  const createdTeamId = readTeamInfoId(profile.team_info);
  return createdTeamId != null && createdTeamId === teamId;
}

/**
 * 当前账号是否为该订单所属团队的管理员。
 * 只认 `user_teams` 里该团队的 role=1，或订单团队就是本人创建的团队。
 * 账号级 `team_admin` / `is_admin` / `team_role` 不能代替某一团队的角色。
 */
export function canViewOrderTeamPrivacy(
  profile: SystemUserInfo | null | undefined,
  orderTeamId?: string | number | null,
  orderTeamName?: string | null,
): boolean {
  if (!profile) return false;
  const teams = getUserTeams(profile);
  const teamId = orderTeamId == null ? '' : String(orderTeamId).trim();
  if (teamId) {
    const membership = teams.find((team) => String(team.teamId) === teamId);
    return membership?.role === 1 || isCreatedTeam(profile, teamId);
  }

  const name = typeof orderTeamName === 'string' ? orderTeamName.trim().toLowerCase() : '';
  if (!name) return false;
  const named = teams.filter((team) => team.teamName.trim().toLowerCase() === name);
  if (named.length !== 1) return false;
  return named[0].role === 1 || isCreatedTeam(profile, String(named[0].teamId));
}

/** @deprecated 使用 isTeamAdmin */
export const isTeamAdminProfile = isTeamAdmin;

/** 从 `team_info.country` 读取团队注册国家（AU / NZ / CA / US 等）。 */
export function readTeamCountry(
  teamInfo: Record<string, unknown> | null | undefined,
): string | undefined {
  if (!teamInfo || typeof teamInfo !== 'object') return undefined;
  return readTeamInfoString(teamInfo, ['country']);
}

/** 从 `team_info.gst_rate` 读取 GST 小数税率（如 0.10 表示 10%）。 */
export function readTeamGstRateDecimal(
  teamInfo: Record<string, unknown> | null | undefined,
): number | null {
  if (!teamInfo || typeof teamInfo !== 'object') return null;
  const raw = teamInfo.gst_rate ?? teamInfo.gstRate;
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n > 0 && n <= 1) return Math.round(n * 10000) / 10000;
  return Math.round((n / 100) * 10000) / 10000;
}

/** 仅自己创建的团队可编辑资料、成员、发票等（以 `team_info` 为准，不依赖当前活跃团队） */
export function canEditTeamInfo(profile: SystemUserInfo | null | undefined): boolean {
  return hasCreatedOwnTeam(profile);
}

function readProfileTimeString(
  profile: SystemUserInfo,
  keys: string[],
): string | undefined {
  const record = profile as unknown as Record<string, unknown>;
  for (const k of keys) {
    const v = record[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return undefined;
}

/** 订阅到期时间：优先 `get_user_info.expire_time` */
export function getTeamSubExpireAt(profile: SystemUserInfo | null | undefined): string | null {
  if (!profile) return null;
  return (
    readProfileTimeString(profile, ['expire_time', 'expireTime']) ??
    (typeof profile.team_sub_expire_time === 'string' && profile.team_sub_expire_time.trim()
      ? profile.team_sub_expire_time.trim()
      : undefined) ??
    readTeamInfoString(profile.team_info ?? {}, ['expire_time', 'expireTime']) ??
    null
  );
}

export function getTeamSubStartAt(profile: SystemUserInfo | null | undefined): string | null {
  if (!profile) return null;
  if (typeof profile.team_sub_start_time === 'string' && profile.team_sub_start_time.trim()) {
    return profile.team_sub_start_time.trim();
  }
  return readTeamInfoString(profile.team_info ?? {}, ['start_time', 'startTime']) ?? null;
}

export function isTeamSubYearly(profile: SystemUserInfo | null | undefined): boolean {
  if (!profile) return false;
  const unit = profile.team_sub_unit;
  if (unit === 1 || unit === '1' || unit === 'year' || unit === 'yearly') return true;
  return false;
}

export function isUserInTeam(profile: SystemUserInfo | null | undefined): boolean {
  if (!profile) return false;
  const role = Number(profile.team_role);
  if (role === 1 || role === 2) return true;
  if (!isTeamInfoEmpty(profile.team_info)) return true;
  return getUserTeamCount(profile) > 0;
}

/** 当前账号订阅是否在有效期内（依据 `get_user_info.expire_time` 等） */
export function hasActiveSubscription(profile: SystemUserInfo | null | undefined): boolean {
  if (!profile) return false;
  const expireAt = getTeamSubExpireAt(profile);
  if (!expireAt) return false;
  const exp = new Date(expireAt);
  if (Number.isNaN(exp.getTime())) return false;
  return exp.getTime() > Date.now();
}

function readBoolFlag(source: Record<string, unknown> | null | undefined, keys: string[]): boolean | undefined {
  if (!source) return undefined;
  for (const key of keys) {
    const raw = source[key];
    if (raw === true || raw === 1 || raw === '1' || raw === 'true') return true;
    if (raw === false || raw === 0 || raw === '0' || raw === 'false') return false;
  }
  return undefined;
}

/**
 * 体验套餐仅第一次创建自己的团队可用，有且只有一次。
 * 已加入其他团队不影响；已创建过自己的团队或已达 5 队上限则不可再用。
 * 不要用加入他人团队带来的 expire_time 判断。
 */
export function canActivateFreeTrial(profile: SystemUserInfo | null | undefined): boolean {
  if (!profile || hasCreatedOwnTeam(profile)) return false;
  if (getUserTeamCount(profile) >= MAX_TEAMS_PER_OPERATOR) return false;

  const record = profile as unknown as Record<string, unknown>;
  const explicitUsed = readBoolFlag(record, [
    'is_free_trial_used',
    'free_trial_used',
    'has_used_free_trial',
    'hasUsedFreeTrial',
  ]);

  if (explicitUsed === true) return false;
  return true;
}

/** 是否有本人有效的建团订阅（不含加入他人团队带来的到期时间） */
export function hasOwnTeamSubscription(profile: SystemUserInfo | null | undefined): boolean {
  if (!profile) return false;
  const record = profile as unknown as Record<string, unknown>;
  const explicit = readBoolFlag(record, ['has_own_subscription', 'hasOwnSubscription']);
  if (explicit !== undefined) return explicit;
  if (!isUserInTeam(profile)) return hasActiveSubscription(profile);
  return false;
}

/** 团队页展示用，由 `get_user_info.team_info` 归一化 */
export interface DisplayTeamInfo {
  name: string;
  teamCode?: string;
  tagline?: string;
  phone?: string;
  email?: string;
  address?: string;
  logoPath?: string;
  foundedAt?: string;
}

/** 将 `team_info` 转为页面可展示结构；无有效团队名时返回 `null` */
export function parseDisplayTeamInfo(
  teamInfo: Record<string, unknown> | null | undefined,
): DisplayTeamInfo | null {
  if (isTeamInfoEmpty(teamInfo)) return null;
  const name = readTeamInfoString(teamInfo!, ['name', 'team_name']);
  if (!name) return null;
  return {
    name,
    teamCode: readTeamInfoString(teamInfo!, ['team_code', 'teamCode']),
    tagline: readTeamInfoString(teamInfo!, ['team_profile', 'tagline']),
    phone: readTeamInfoString(teamInfo!, ['phone']),
    email: readTeamInfoString(teamInfo!, ['email']),
    address: readTeamInfoString(teamInfo!, ['address', 'fullAddress']),
    logoPath: readTeamInfoString(teamInfo!, ['logo_path', 'logoPath']),
    foundedAt: readTeamInfoString(teamInfo!, ['create_date', 'foundedAt']),
  };
}

/** 读取 `team_info` 的团队 ID（用于与 `user_teams` 对齐） */
export function readTeamInfoId(
  teamInfo: Record<string, unknown> | null | undefined,
): string | null {
  if (!teamInfo || typeof teamInfo !== 'object') return null;
  const raw = teamInfo.id ?? teamInfo.team_id ?? teamInfo.teamId;
  if (raw === null || raw === undefined || raw === '') return null;
  return String(raw);
}

/** 所选 `user_teams` 条目是否为团长（管理员） */
export function isSelectedTeamAdmin(team: UserTeamMembership | null | undefined): boolean {
  return team?.role === 1;
}

/** 是否为自己创建的团队（对齐 `team_info`，否则看 role=1） */
export function isOwnCreatedUserTeam(
  team: UserTeamMembership | null | undefined,
  profile?: SystemUserInfo | null,
): boolean {
  if (!team) return false;
  const createdTeamId = profile ? readTeamInfoId(profile.team_info) : null;
  if (createdTeamId != null) return String(team.teamId) === createdTeamId;
  return team.role === 1 || team.isAdmin === true;
}

/** 将 `user_teams` 条目转为展示结构；若与当前 `team_info` 同队则优先用更完整的 team_info */
export function displayTeamFromMembership(
  team: UserTeamMembership,
  profile?: SystemUserInfo | null,
): DisplayTeamInfo {
  const activeInfo = profile ? parseDisplayTeamInfo(profile.team_info) : null;
  const activeId = profile ? readTeamInfoId(profile.team_info) : null;
  const sameAsActive = activeId != null && String(team.teamId) === activeId;

  if (sameAsActive && activeInfo) {
    return {
      ...activeInfo,
      name: activeInfo.name || team.teamName,
      teamCode: activeInfo.teamCode ?? team.teamCode,
    };
  }

  return {
    name: team.teamName,
    teamCode: team.teamCode,
    tagline: team.tagline,
    phone: team.phone,
    email: team.email,
    address: team.address,
    logoPath: team.logoPath,
  };
}

/** 默认选中的团队：优先对齐当前 `team_info`，否则第一支 */
export function resolveDefaultUserTeam(
  profile: SystemUserInfo | null | undefined,
): UserTeamMembership | null {
  const teams = getUserTeams(profile);
  if (teams.length === 0) return null;
  const activeId = profile ? readTeamInfoId(profile.team_info) : null;
  if (activeId) {
    const matched = teams.find((t) => String(t.teamId) === activeId);
    if (matched) return matched;
  }
  return teams[0] ?? null;
}

/** 是否还可再加入团队（含创建）：未达上限 */
export function canJoinAnotherTeam(profile: SystemUserInfo | null | undefined): boolean {
  if (!profile) return false;
  return getUserTeamCount(profile) < MAX_TEAMS_PER_OPERATOR;
}

/** 账号当前没有任何团队时，应提醒创建或加入（订单/团队空态） */
export function needsTeamPrompt(profile: SystemUserInfo | null | undefined): boolean {
  if (!profile) return false;
  return !isUserInTeam(profile);
}

/**
 * Me 页 Join Team / Create Team 提示：
 * 已创建过自己的团队则不显示；未创建且未达 5 队上限时显示。
 */
export function needsJoinOrCreatePrompt(profile: SystemUserInfo | null | undefined): boolean {
  if (!profile) return false;
  if (hasCreatedOwnTeam(profile)) return false;
  return canJoinAnotherTeam(profile);
}

/** 未达上限时可再加入其他团队，或创建自己尚未创建的唯一团队 */
export function canCreateOrJoinTeam(profile: SystemUserInfo | null | undefined): boolean {
  if (!profile) return false;
  if (!canJoinAnotherTeam(profile)) return false;
  return true;
}

/**
 * 是否还可创建自己的团队：
 * - 每个账号仅一次创建机会（加入别人的团队不占用）
 * - 加入团队总数未达上限
 * 不要求先购买订阅；首次建团由后端自动赠送体验。
 */
export function canCreateTeam(profile: SystemUserInfo | null | undefined): boolean {
  if (!profile) return false;
  if (hasCreatedOwnTeam(profile)) return false;
  if (!canJoinAnotherTeam(profile)) return false;
  return true;
}

interface GetUserInfoHttpResponse {
  error?: boolean;
  message?: string;
  data?: SystemUserInfo;
}

let inflightSystemUserInfo: Promise<SystemUserInfo> | null = null;

export async function fetchSystemUserInfo(): Promise<SystemUserInfo> {
  if (inflightSystemUserInfo) return inflightSystemUserInfo;
  inflightSystemUserInfo = http
    .post<GetUserInfoHttpResponse>('/system/get_user_info')
    .then((res) => {
      if (!res.data) {
        throw new ApiRequestError('Failed to load user profile.');
      }
      return res.data;
    })
    .finally(() => {
      inflightSystemUserInfo = null;
    });
  return inflightSystemUserInfo;
}

export async function postUpdateUserInfo(body: UpdateUserInfoBody): Promise<void> {
  await http.post('/system/user/update_info', body);
}

/** 解析 `get_user_info.postal_code_lst` 或增删接口返回的 `postal_codes` */
export function parsePostalCodeList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const codes: string[] = [];
  for (const item of raw) {
    const code = typeof item === 'string' || typeof item === 'number' ? String(item).trim() : '';
    if (!code || seen.has(code)) continue;
    seen.add(code);
    codes.push(code);
  }
  return codes;
}

/** 当前账号已设置的服务地区邮编 */
export function getUserPostalCodes(profile: SystemUserInfo | null | undefined): string[] {
  if (!profile) return [];
  return parsePostalCodeList(profile.postal_code_lst);
}

export interface PostalCodeMutationResult {
  postalCodes: string[];
  count: number;
  added?: boolean;
  removed?: boolean;
}

interface PostalCodeMutationHttpResponse {
  data?: {
    postal_codes?: unknown;
    count?: number;
    added?: boolean;
    removed?: boolean;
  };
}

function readPostalCodeMutation(res: PostalCodeMutationHttpResponse): PostalCodeMutationResult {
  const postalCodes = parsePostalCodeList(res.data?.postal_codes);
  return {
    postalCodes,
    count: typeof res.data?.count === 'number' ? res.data.count : postalCodes.length,
    added: res.data?.added,
    removed: res.data?.removed,
  };
}

/** POST `/system/servicer/add_postal_code` — 新增一个服务地区邮编 */
export async function postAddServicerPostalCode(postalCode: string): Promise<PostalCodeMutationResult> {
  const res = await http.post<PostalCodeMutationHttpResponse>(
    '/system/servicer/add_postal_code',
    { postal_code: postalCode },
    { skipApiEnvelopeModal: true },
  );
  return readPostalCodeMutation(res);
}

/** POST `/system/servicer/remove_postal_code` — 删除一个服务地区邮编 */
export async function postRemoveServicerPostalCode(postalCode: string): Promise<PostalCodeMutationResult> {
  const res = await http.post<PostalCodeMutationHttpResponse>(
    '/system/servicer/remove_postal_code',
    { postal_code: postalCode },
    { skipApiEnvelopeModal: true },
  );
  return readPostalCodeMutation(res);
}

export interface UpdateAvatarResult {
  avatarUrl?: string;
}

interface UpdateAvatarHttpResponse {
  data: {
    avatarUrl?: string;
  };
}

/** POST `multipart/form-data`, field `avatar`. */
export async function postUpdateAvatar(avatar: Blob, filename = 'avatar.jpg'): Promise<UpdateAvatarResult> {
  const formData = new FormData();
  const name = avatar instanceof File && avatar.name ? avatar.name : filename;
  formData.append('avatar', avatar, name);
  const { data: json } = await apiClient.post<UpdateAvatarHttpResponse>('/system/user/update_avatar', formData);
  return { avatarUrl: json.data.avatarUrl };
}
